/**
 * useUserManagement Hook
 * 
 * Custom React hook for managing user data and operations.
 * Provides state management, loading states, error handling,
 * and methods for user CRUD operations.
 * 
 * @module useUserManagement
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserManagementService } from '../services/UserManagementService';
import {
  UserProfile,
  CreateUserData,
  UpdateUserData,
  UserFilters,
  UserSort,
  UserActionResult,
  BulkUserAction,
  UserStatistics,
  UserRole,
  UserStatus
} from '../@types/user';

/**
 * Hook return type
 */
interface UseUserManagementReturn {
  // Data
  users: UserProfile[];
  userStatistics: UserStatistics | null;
  selectedUsers: string[];
  
  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isStatisticsLoading: boolean;
  
  // Error states
  error: string | null;
  
  // Pagination
  hasMore: boolean;
  currentPage: number;
  
  // Methods
  fetchUsers: (filters?: UserFilters, sort?: UserSort, reset?: boolean) => Promise<void>;
  fetchUserStatistics: () => Promise<void>;
  createUser: (userData: CreateUserData) => Promise<UserActionResult>;
  updateUser: (userId: string, userData: UpdateUserData) => Promise<UserActionResult>;
  deleteUser: (userId: string, reason?: string) => Promise<UserActionResult>;
  resetUserPassword: (userId: string) => Promise<UserActionResult>;
  performBulkAction: (bulkAction: BulkUserAction) => Promise<UserActionResult[]>;
  selectUser: (userId: string) => void;
  selectAllUsers: () => void;
  clearSelection: () => void;
  clearError: () => void;
  refreshUsers: () => Promise<void>;
}

/**
 * useUserManagement Hook
 * 
 * @param initialFilters - Initial filters to apply
 * @param initialSort - Initial sort configuration
 * @param pageSize - Number of users per page
 * @returns User management state and methods
 * 
 * @example
 * ```tsx
 * const {
 *   users,
 *   isLoading,
 *   error,
 *   fetchUsers,
 *   createUser,
 *   updateUser,
 *   deleteUser
 * } = useUserManagement();
 * ```
 */
export const useUserManagement = (
  initialFilters?: UserFilters,
  initialSort?: UserSort,
  pageSize: number = 50
): UseUserManagementReturn => {
  const { user } = useAuth();
  
  // State
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [userStatistics, setUserStatistics] = useState<UserStatistics | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStatisticsLoading, setIsStatisticsLoading] = useState(false);
  
  // Error state
  const [error, setError] = useState<string | null>(null);
  
  // Current filters and sort
  const [currentFilters, setCurrentFilters] = useState<UserFilters | undefined>(initialFilters);
  const [currentSort, setCurrentSort] = useState<UserSort | undefined>(initialSort);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch users with filters and sorting
   */
  const fetchUsers = useCallback(async (
    filters?: UserFilters,
    sort?: UserSort,
    reset: boolean = false
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const activeFilters = filters || currentFilters;
      const activeSort = sort || currentSort;
      
      // Update current filters and sort
      if (filters) setCurrentFilters(filters);
      if (sort) setCurrentSort(sort);

      const result = await UserManagementService.getUsers(
        activeFilters,
        activeSort,
        pageSize
      );

      if (reset) {
        setUsers(result.users);
        setCurrentPage(1);
      } else {
        setUsers(prev => [...prev, ...result.users]);
        setCurrentPage(prev => prev + 1);
      }
      
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, [pageSize]); // Removed currentFilters and currentSort from dependencies

  /**
   * Fetch user statistics
   */
  const fetchUserStatistics = useCallback(async () => {
    try {
      setIsStatisticsLoading(true);
      setError(null);
      
      const stats = await UserManagementService.getUserStatistics();
      setUserStatistics(stats);
    } catch (err) {
      console.error('Error fetching user statistics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch user statistics');
    } finally {
      setIsStatisticsLoading(false);
    }
  }, []);

  /**
   * Create a new user
   */
  const createUser = useCallback(async (userData: CreateUserData): Promise<UserActionResult> => {
    if (!user?.id) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    try {
      setIsCreating(true);
      setError(null);
      
      const result = await UserManagementService.createUser(userData, user.id);
      
      if (result.success) {
        // Refresh users list
        await fetchUsers(currentFilters, currentSort, true);
        // Refresh statistics
        await fetchUserStatistics();
      } else {
        setError(result.error || 'Failed to create user');
      }
      
      return result;
    } catch (err) {
      console.error('Error creating user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsCreating(false);
    }
  }, [user?.id, fetchUsers, fetchUserStatistics]);

  /**
   * Update an existing user
   */
  const updateUser = useCallback(async (
    userId: string,
    userData: UpdateUserData
  ): Promise<UserActionResult> => {
    if (!user?.id) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    try {
      setIsUpdating(true);
      setError(null);
      
      const result = await UserManagementService.updateUser(userId, userData, user.id);
      
      if (result.success) {
        // Update user in local state
        setUsers(prev => prev.map(u => 
          u.id === userId ? { ...u, ...userData, updatedAt: new Date() } : u
        ));
        // Refresh statistics
        await fetchUserStatistics();
      } else {
        setError(result.error || 'Failed to update user');
      }
      
      return result;
    } catch (err) {
      console.error('Error updating user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsUpdating(false);
    }
  }, [user?.id, fetchUserStatistics]);

  /**
   * Delete a user (soft delete)
   */
  const deleteUser = useCallback(async (
    userId: string,
    reason?: string
  ): Promise<UserActionResult> => {
    if (!user?.id) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    try {
      setIsDeleting(true);
      setError(null);
      
      const result = await UserManagementService.deleteUser(userId, user.id, reason);
      
      if (result.success) {
        // Remove user from local state
        setUsers(prev => prev.filter(u => u.id !== userId));
        // Remove from selection
        setSelectedUsers(prev => prev.filter(id => id !== userId));
        // Refresh statistics
        await fetchUserStatistics();
      } else {
        setError(result.error || 'Failed to delete user');
      }
      
      return result;
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsDeleting(false);
    }
  }, [user?.id, fetchUserStatistics]);

  /**
   * Reset user password
   */
  const resetUserPassword = useCallback(async (userId: string): Promise<UserActionResult> => {
    if (!user?.id) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    try {
      setError(null);
      
      const result = await UserManagementService.resetUserPassword(userId, user.id);
      
      if (!result.success) {
        setError(result.error || 'Failed to reset password');
      }
      
      return result;
    } catch (err) {
      console.error('Error resetting password:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [user?.id]);

  /**
   * Perform bulk action on multiple users
   */
  const performBulkAction = useCallback(async (
    bulkAction: BulkUserAction
  ): Promise<UserActionResult[]> => {
    if (!user?.id) {
      return [{
        success: false,
        error: 'User not authenticated'
      }];
    }

    try {
      setError(null);
      
      const results = await UserManagementService.performBulkAction(bulkAction, user.id);
      
      // Check if any actions failed
      const failedActions = results.filter(r => !r.success);
      if (failedActions.length > 0) {
        const errorMessages = failedActions.map(r => r.error).filter(Boolean);
        setError(`Some actions failed: ${errorMessages.join(', ')}`);
      }
      
      // Refresh users list if any actions succeeded
      const successfulActions = results.filter(r => r.success);
      if (successfulActions.length > 0) {
        await fetchUsers(currentFilters, currentSort, true);
        await fetchUserStatistics();
      }
      
      return results;
    } catch (err) {
      console.error('Error performing bulk action:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to perform bulk action';
      setError(errorMessage);
      return [{
        success: false,
        error: errorMessage
      }];
    }
  }, [user?.id, fetchUsers, fetchUserStatistics]);

  /**
   * Select a user
   */
  const selectUser = useCallback((userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }, []);

  /**
   * Select all users
   */
  const selectAllUsers = useCallback(() => {
    setSelectedUsers(users.map(u => u.id));
  }, [users]);

  /**
   * Clear user selection
   */
  const clearSelection = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  /**
   * Refresh users list
   */
  const refreshUsers = useCallback(async () => {
    await fetchUsers(currentFilters, currentSort, true);
  }, [fetchUsers]);

  // Load initial data
  useEffect(() => {
    fetchUsers(initialFilters, initialSort, true);
      fetchUserStatistics();
  }, []); // Only run on mount

  return {
    // Data
    users,
    userStatistics,
    selectedUsers,
    
    // Loading states
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isStatisticsLoading,
    
    // Error states
    error,
    
    // Pagination
    hasMore,
    currentPage,
    
    // Methods
    fetchUsers,
    fetchUserStatistics,
    createUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    performBulkAction,
    selectUser,
    selectAllUsers,
    clearSelection,
    clearError,
    refreshUsers
  };
};